// Welle 4 — AI Vision queue: checked / excluded / waiting, fully visible.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye } from "lucide-react";
import { fetchVisionQueue } from "../../../api/adminDashboardApi";
import { SectionCard, EmptyHint, Pill, formatDateTime } from "./AdminV2Kit";

const TABS = ["pending", "checked", "flagged"];

export default function VisionQueueCard({ summary, developer = false }) {
  const { t } = useTranslation("admin");
  const [tab, setTab] = useState("pending");
  const [lists, setLists] = useState(null);
  const counts = {
    checked: summary?.checked_total ?? 0,
    flagged: summary?.flagged_total ?? 0,
    pending: summary?.pending_candidates ?? 0,
  };

  useEffect(() => {
    let alive = true;
    fetchVisionQueue(developer ? 30 : 8)
      .then((d) => alive && setLists(d))
      .catch(() => {});
    return () => { alive = false; };
  }, [developer]);

  const items = lists?.[tab] || [];
  const emptyKey = {
    checked: "emptyChecked",
    flagged: "emptyFlagged",
    pending: "emptyPending",
  }[tab];

  return (
    <SectionCard
      title={t("modules.vision_queue.title")}
      icon={Eye}
      status={counts.flagged > 0 ? "attention" : "healthy"}
      testid="adminv2-vision-card"
      actions={
        summary?.last_checked_at ? (
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            {t("modules.vision_queue.lastCheck")}: {formatDateTime(summary.last_checked_at)}
          </span>
        ) : null
      }
    >
      <div className="grid grid-cols-3 gap-2">
        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`adminv2-vision-tab-${k}`}
            onClick={() => setTab(k)}
            className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
              tab === k
                ? "border-amber-500/50 bg-amber-500/10"
                : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-700"
            }`}
          >
            <div className="text-xl font-semibold text-zinc-100 tabular-nums">
              {counts[k]}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t(`modules.vision_queue.tab${k[0].toUpperCase()}${k.slice(1)}`)}
            </div>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyHint testid={`adminv2-vision-empty-${tab}`}>
          {t(`modules.vision_queue.${emptyKey}`)}
        </EmptyHint>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={it.ulid}
              data-testid={`adminv2-vision-item-${it.ulid}`}
              className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2"
            >
              {it.primary_image_url ? (
                <img
                  src={it.primary_image_url}
                  alt=""
                  loading="lazy"
                  className="h-9 w-12 rounded-md object-cover border border-zinc-800"
                />
              ) : (
                <div className="h-9 w-12 rounded-md bg-zinc-900 border border-zinc-800" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-zinc-200">{it.title || it.ulid}</div>
                <div className="text-[11px] text-zinc-500">
                  {it.country}
                  {it.price ? ` · €${Math.round(it.price).toLocaleString("de-AT")}` : ""}
                  {developer && it.vision_damage_checked_at
                    ? ` · ${formatDateTime(it.vision_damage_checked_at)}`
                    : ""}
                </div>
              </div>
              {tab === "flagged" ? (
                <Pill tone="rose">
                  {t("modules.damage_queue.reasons.vision_damage_suspected")}
                </Pill>
              ) : it.damage_condition && it.damage_condition !== "healthy" ? (
                <Pill tone="amber">{it.damage_condition}</Pill>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
